import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  modelRegistryApi,
  registryErrorMessage,
} from "@/services/modelRegistryApi";
import {
  MODALITIES,
  MODEL_APIS,
  MODEL_STATES,
  type AdminModel,
  type Modality,
  type ModelApi,
  type ModelProvider,
  type ModelState,
  type VendorCatalogEntry,
  type VendorSnapshot,
} from "@/types/modelRegistry";
import { STATE_LABELS } from "./format";
import {
  emptyForm,
  formFrom,
  isComplete,
  prefillFromVendor,
  slugFrom,
  toWrite,
  type ModelForm,
} from "./model-form";
import { RoutingFields } from "./RoutingFields";
import { Btn, Drawer, Field, SelectInput, TextArea, TextInput } from "./ui";
import { VendorPicker } from "./VendorPicker";

interface ModelDrawerProps {
  open: boolean;
  onClose: () => void;
  model: AdminModel | null;
  providers: ModelProvider[];
  onSaved: () => Promise<void>;
}

const OPENROUTER = "openrouter";

/** The vendor snapshot for the model in the form. An existing row's stored snapshot is used until a fresh one lands. */
function useVendorSnapshot(
  modelName: string | null,
  stored: VendorSnapshot | null,
) {
  const [snapshot, setSnapshot] = useState<VendorSnapshot | undefined>(
    stored ?? undefined,
  );
  useEffect(() => {
    setSnapshot(stored ?? undefined);
    if (!modelName) return;
    let live = true;
    modelRegistryApi
      .vendorLookup(modelName)
      .then((found) => live && found && setSnapshot(found))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [modelName, stored]);
  return snapshot;
}

const vendorHint = (
  label: string,
  value: number | undefined,
): string | undefined =>
  value ? `${label}: ${value.toLocaleString()}` : undefined;

export function ModelDrawer({
  open,
  onClose,
  model,
  providers,
  onSaved,
}: ModelDrawerProps) {
  const defaultProvider =
    providers.find((p) => p.slug === OPENROUTER)?.id ?? providers[0]?.id ?? "";
  const [form, setForm] = useState<ModelForm>(() =>
    model ? formFrom(model) : emptyForm(defaultProvider),
  );
  const [busy, setBusy] = useState(false);
  const viaOpenRouter =
    providers.find((p) => p.id === form.providerId)?.slug === OPENROUTER;
  const vendor = useVendorSnapshot(
    viaOpenRouter && form.modelName ? form.modelName : null,
    model?.vendor ?? null,
  );
  const vendorCost =
    vendor?.cost && "input" in vendor.cost ? vendor.cost : undefined;

  useEffect(() => {
    if (open) setForm(model ? formFrom(model) : emptyForm(defaultProvider));
  }, [open, model, defaultProvider]);

  useEffect(() => {
    if (vendor) setForm((current) => prefillFromVendor(current, vendor));
  }, [vendor]);

  const set = <K extends keyof ModelForm>(key: K, value: ModelForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const pickVendor = (entry: VendorCatalogEntry) =>
    setForm((current) => ({
      ...emptyForm(current.providerId),
      state: current.state,
      displayOrder: current.displayOrder,
      modelName: entry.modelName,
      name: current.name || entry.name,
      slug: current.slug || slugFrom(entry.modelName),
      api: entry.kind === "image" ? "openrouter-images" : "openai-completions",
    }));

  const toggleModality = (modality: Modality, on: boolean) =>
    set(
      "input",
      on ? [...form.input, modality] : form.input.filter((m) => m !== modality),
    );

  const save = async () => {
    setBusy(true);
    try {
      const body = toWrite(form, vendor, viaOpenRouter);
      if (model) await modelRegistryApi.updateModel(model.id, body);
      else await modelRegistryApi.createModel(body);
      toast.success(model ? `${body.name} saved` : `${body.name} added`);
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(registryErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={model ? `Edit ${model.name}` : "Add a model"}
      description="Values come from the vendor and follow its updates. Change one and it stays as you set it."
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Btn>
          <Btn onClick={() => void save()} disabled={!isComplete(form) || busy}>
            {model ? "Save changes" : "Add model"}
          </Btn>
        </>
      }
    >
      <Field label="Provider">
        <SelectInput
          value={form.providerId}
          onChange={(e) => set("providerId", e.target.value)}
          options={providers.map((p) => ({ value: p.id, label: p.name }))}
        />
      </Field>

      <Field
        label="Vendor model"
        hint={
          viaOpenRouter
            ? "Pick from OpenRouter to fill the rest."
            : "The id the provider expects."
        }
      >
        {viaOpenRouter ? (
          <VendorPicker value={form.modelName} onPick={pickVendor} />
        ) : (
          <TextInput
            value={form.modelName}
            onChange={(e) => set("modelName", e.target.value)}
            placeholder="claude-sonnet-5"
            className="font-mono"
          />
        )}
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Name" hint="What users see.">
          <TextInput
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field label="Slug" hint="Stable handle. Cannot change later.">
          <TextInput
            value={form.slug}
            onChange={(e) => set("slug", e.target.value)}
            disabled={!!model}
            className="font-mono"
          />
        </Field>
      </div>

      <Field label="Description">
        <TextArea
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="One line under the name"
          rows={2}
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="API">
          <SelectInput
            value={form.api}
            onChange={(e) => set("api", e.target.value as ModelApi)}
            options={MODEL_APIS.map((api) => ({ value: api, label: api }))}
          />
        </Field>
        <Field label="State">
          <SelectInput
            value={form.state}
            onChange={(e) => set("state", e.target.value as ModelState)}
            options={MODEL_STATES.map((state) => ({
              value: state,
              label: STATE_LABELS[state].label,
            }))}
          />
        </Field>
      </div>

      <Field
        label="Reads"
        hint={vendor?.input ? `Vendor: ${vendor.input.join(", ")}` : undefined}
      >
        <div className="flex gap-4">
          {MODALITIES.map((modality) => (
            <label
              key={modality}
              className="flex items-center gap-2 text-sm capitalize text-gray-800"
            >
              <input
                type="checkbox"
                className="size-4 rounded border-gray-300 accent-gray-900"
                checked={form.input.includes(modality)}
                onChange={(e) => toggleModality(modality, e.target.checked)}
              />
              {modality}
            </label>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Context window"
          hint={vendorHint("Vendor", vendor?.contextWindow)}
        >
          <TextInput
            inputMode="numeric"
            value={form.contextWindow}
            onChange={(e) => set("contextWindow", e.target.value)}
            placeholder="tokens"
            className="tabular-nums"
          />
        </Field>
        <Field
          label="Max output"
          hint={vendorHint("Vendor", vendor?.maxOutputTokens)}
        >
          <TextInput
            inputMode="numeric"
            value={form.maxOutputTokens}
            onChange={(e) => set("maxOutputTokens", e.target.value)}
            placeholder="tokens"
            className="tabular-nums"
          />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Field
          label="Input $ per 1M"
          hint={vendorHint("Vendor", vendorCost?.input)}
        >
          <TextInput
            inputMode="decimal"
            value={form.costInput}
            onChange={(e) => set("costInput", e.target.value)}
            className="tabular-nums"
          />
        </Field>
        <Field
          label="Output $ per 1M"
          hint={vendorHint("Vendor", vendorCost?.output)}
        >
          <TextInput
            inputMode="decimal"
            value={form.costOutput}
            onChange={(e) => set("costOutput", e.target.value)}
            className="tabular-nums"
          />
        </Field>
        <Field
          label="Reasoning"
          hint={
            vendor?.reasoning === undefined
              ? undefined
              : `Vendor: ${vendor.reasoning ? "yes" : "no"}`
          }
        >
          <SelectInput
            value={form.reasoning}
            onChange={(e) =>
              set("reasoning", e.target.value as ModelForm["reasoning"])
            }
            options={[
              { value: "", label: "Unknown" },
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
          />
        </Field>
      </div>

      {viaOpenRouter && (
        <RoutingFields
          modelName={form.modelName}
          routing={form.routing}
          onChange={(routing) => set("routing", routing)}
        />
      )}

      <Field label="Order in picker" hint="Lower comes first.">
        <TextInput
          inputMode="numeric"
          value={form.displayOrder}
          onChange={(e) => set("displayOrder", e.target.value)}
          className="w-24 tabular-nums"
        />
      </Field>
    </Drawer>
  );
}
